import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import RivieraMapa from './RivieraMapa';
import FaixaOferta from './FaixaOferta';
import { ReservaNav, useNoIndex } from './RivieraInventario';
import { PROVA_RESERVA } from './reserva';

/*
  LP Riviera de Santa Cristina XIII (Momentum) — v1.
  SPEC: bianca/specs/SPEC_59_LP_RIVIERA_SANTA_CRISTINA.md
  Paleta/tipografia: guia de marca Momentum (azul #005BAA, laranja #F58220 = vela do logo,
  verde #80C342, cyan #00B9F2). Título em Cormorant Garamond (≈ Cronos Pro), corpo em Inter.
  v1 NÃO inclui: mapa de lotes interativo (adiado), EntornoMapa MapLibre.
  O form envia para /api/riviera-lead, que apenas notifica novo_lead.
*/

const C = {
  azul: '#005BAA',
  azulEscuro: '#0a3d6b',
  laranja: '#F58220',
  verde: '#80C342',
  cyan: '#00B9F2',
  cinza: '#808285',
  cream: '#f7fbfd',
  tinta: '#13314e',
};

/* ---------- ÍCONES SVG (sem emoji) ---------- */
const SvgIcon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'inline-block', verticalAlign: 'middle' }} aria-hidden="true">{children}</svg>
);
const I = {
  lock: <SvgIcon><rect x="4" y="10.5" width="16" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></SvgIcon>,
  shield: <SvgIcon><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></SvgIcon>,
  trending: <SvgIcon><polyline points="3 16 9 10 13 14 21 6" /><polyline points="15 6 21 6 21 12" /></SvgIcon>,
  leaf: <SvgIcon><path d="M5 20c0-9 6-15 15-15 0 9-6 15-15 15z" /><path d="M5 20c4-5 8-7 12-8" /></SvgIcon>,
  home: <SvgIcon><path d="M4 11l8-7 8 7" /><path d="M6 10v10h12V10" /><path d="M10 20v-6h4v6" /></SvgIcon>,
  anchor: <SvgIcon><circle cx="12" cy="5" r="2.4" /><path d="M12 7.4V21" /><path d="M5 12a7 7 0 0 0 14 0" /><path d="M4 12h2M18 12h2" /></SvgIcon>,
  coffee: <SvgIcon><path d="M4 8h12v4a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z" /><path d="M16 9h2.5a2.5 2.5 0 0 1 0 5H16" /><path d="M7 3v2M11 3v2" /></SvgIcon>,
  pizza: <SvgIcon><path d="M12 4l8.5 4C19 16 15 20 12 20S5 16 3.5 8z" /><circle cx="10" cy="10" r="0.9" fill="currentColor" stroke="none" /><circle cx="13.5" cy="12.5" r="0.9" fill="currentColor" stroke="none" /></SvgIcon>,
  cart: <SvgIcon><circle cx="9" cy="20" r="1.3" /><circle cx="17" cy="20" r="1.3" /><path d="M3 4h2l2.2 11h10l2-7H6.5" /></SvgIcon>,
  utensils: <SvgIcon><path d="M7 3v18" /><path d="M5 3v4M9 3v4M5 7h4" /><path d="M16 21V13c-2-1-2-8 1-10" /></SvgIcon>,
  gift: <SvgIcon><rect x="4" y="9" width="16" height="11" rx="1" /><path d="M4 13h16M12 9v11" /><path d="M12 9C12 6 10 4 8 5.2 6.8 6 9 9 12 9z" /><path d="M12 9c0-3 2-5 4-3.8 1.2.8-1 3.8-4 3.8z" /></SvgIcon>,
  instagram: <SvgIcon><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" /></SvgIcon>,
  images: <SvgIcon><rect x="3" y="4" width="18" height="14" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M21 15l-5-4-7 6" /></SvgIcon>,
  rotate: <SvgIcon><path d="M21 12a9 9 0 1 1-2.6-6.3" /><polyline points="21 4 21 9 16 9" /></SvgIcon>,
  drag: <SvgIcon><polyline points="8 9 5 12 8 15" /><polyline points="16 9 19 12 16 15" /><line x1="5" y1="12" x2="19" y2="12" /></SvgIcon>,
  play: <SvgIcon><path d="M8 5v14l11-7z" fill="currentColor" stroke="none" /></SvgIcon>,
  chevL: <SvgIcon><polyline points="15 5 8 12 15 19" /></SvgIcon>,
  chevR: <SvgIcon><polyline points="9 5 16 12 9 19" /></SvgIcon>,
  close: <SvgIcon><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></SvgIcon>,
  sun: <SvgIcon><circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" /></SvgIcon>,
  chat: <SvgIcon><path d="M4 5h16v11H9l-4 4V5z" /></SvgIcon>,
  pin: <SvgIcon><path d="M12 21s7-6.3 7-12a7 7 0 1 0-14 0c0 5.7 7 12 7 12z" /><circle cx="12" cy="9" r="2.5" /></SvgIcon>,
  calendar: <SvgIcon><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></SvgIcon>,
};

const Page = styled.main`
  background: ${C.cream};
  color: ${C.tinta};
  font-family: 'Inter', system-ui, sans-serif;
  overflow-x: hidden;
  color-scheme: light; /* impede o browser de aplicar UA-dark em inputs/botões */
`;

/* ---------- HERO ---------- */
const Hero = styled.section`
  position: relative;
  min-height: 100svh;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
  background: #06243d;
`;
const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  background: #06243d;
`;
const HeroPoster = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  pointer-events: none;
  transition: opacity 600ms ease;
  will-change: transform, opacity;
  animation: heroKenBurns 16s ease-in-out infinite alternate;
  @keyframes heroKenBurns {
    0%   { transform: scale(1.0)  translate3d(0, 0, 0); }
    100% { transform: scale(1.08) translate3d(-1%, -1%, 0); }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const HeroOverlay = styled.div`
  position: absolute; inset: 0; z-index: 1; pointer-events: none;
  background: linear-gradient(180deg,
    rgba(3,24,43,0.45) 0%, rgba(3,24,43,0.10) 26%,
    rgba(3,24,43,0.05) 55%, rgba(3,24,43,0.45) 82%, rgba(3,24,43,0.86) 100%);
`;
const HeroTopBar = styled.div`
  position: absolute;
  top: 22px; left: 0; right: 0;
  z-index: 3;
  text-align: center;
  pointer-events: none;
  @media (min-width: 768px) { top: 32px; }
`;
const HeroContent = styled(motion.div)`
  position: relative; z-index: 2; text-align: center; padding: 0 24px 64px;
  color: #fff;
  @media (min-width: 768px) { padding-bottom: 96px; }
`;
const Eyebrow = styled.div`
  font-size: 11px; font-weight: 600; letter-spacing: 0.42em; text-transform: uppercase;
  margin-bottom: 22px; color: #fff;
  text-shadow: 0 1px 4px rgba(0,0,0,0.75), 0 0 18px rgba(0,0,0,0.5);
  @media (min-width: 768px) { font-size: 12px; }
`;
const HeroTitle = styled.h1`
  font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif;
  font-style: italic; font-weight: 600;
  font-size: clamp(40px, 9vw, 96px); line-height: 0.98; margin: 0;
  letter-spacing: 0.01em; text-shadow: 0 4px 40px rgba(0,0,0,0.45);
`;
const GoogleHeroTitle = styled(HeroTitle)`
  max-width: 940px;
  font-size: clamp(38px, 8vw, 80px);
`;
const Sail = styled.span`color: ${C.laranja}; font-style: normal; font-weight: 700;`;
const Tagline = styled.div`
  font-family: 'Cormorant Garamond', serif; font-style: italic;
  font-size: clamp(18px, 3.6vw, 28px); margin-top: 14px; color: ${C.cyan};
  letter-spacing: 0.04em;
`;
const HeroSub = styled.div`
  font-size: 11px; font-weight: 400; letter-spacing: 0.32em; text-transform: uppercase;
  opacity: 0.86; margin-top: 26px;
  @media (min-width: 768px) { font-size: 13px; }
`;
const GoogleHeroSub = styled(HeroSub)`
  max-width: 760px;
  margin-right: auto;
  margin-left: auto;
  font-size: clamp(14px, 3.5vw, 20px);
  line-height: 1.45;
  letter-spacing: 0.06em;
  text-transform: none;
  opacity: 0.96;
`;
const GoogleOfferChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;

  span {
    padding: 8px 12px;
    border: 1px solid rgba(255,255,255,0.34);
    border-radius: 999px;
    background: rgba(3,24,43,0.55);
    color: #fff;
    font-size: 12px;
    font-weight: 650;
    letter-spacing: 0.02em;
    backdrop-filter: blur(4px);
  }

  @media (min-width: 768px) {
    gap: 10px;
    span { padding: 9px 15px; font-size: 13px; }
  }
`;
const HeroCTA = styled.a`
  display: inline-block; margin-top: 32px;
  padding: 16px 30px; border-radius: 999px;
  background: ${C.laranja}; color: #fff;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 14px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; text-decoration: none;
  box-shadow: 0 14px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.08);
  transition: transform .15s ease, filter .15s ease, box-shadow .15s ease;
  &:hover {
    transform: translateY(-2px); filter: brightness(1.06);
    box-shadow: 0 18px 38px rgba(0,0,0,0.4);
  }
  @media (min-width: 768px) { font-size: 15px; padding: 18px 34px; margin-top: 38px; }
`;

/* ---------- INTERLÚDIO ---------- */
const Interlude = styled.section`
  text-align: center; padding: 56px 24px 40px; background: ${C.cream};
`;
const IntLine1 = styled.div`
  font-family: 'Cormorant Garamond', serif; font-style: italic; font-weight: 500;
  font-size: clamp(20px, 4.4vw, 30px); color: ${C.cinza};
`;
const IntLine2 = styled.div`
  font-family: 'Cormorant Garamond', serif; font-style: italic; font-weight: 700;
  font-size: clamp(34px, 8vw, 64px); color: ${C.azul}; line-height: 1;
`;

/* ---------- SEÇÕES GENÉRICAS ---------- */
const Section = styled.section`
  padding: 64px 22px;
  max-width: 1180px; margin: 0 auto;
  @media (min-width: 768px) { padding: 88px 32px; }
`;
const Kicker = styled.div`
  font-size: 11px; font-weight: 600; letter-spacing: 0.36em; text-transform: uppercase;
  color: ${C.laranja}; margin-bottom: 14px; text-align: center;
`;
const H2 = styled.h2`
  font-family: 'Cormorant Garamond', serif; font-weight: 700;
  font-size: clamp(28px, 6vw, 48px); color: ${C.azul}; text-align: center;
  margin: 0 0 12px; line-height: 1.05;
`;
const Lead = styled.p`
  text-align: center; max-width: 680px; margin: 0 auto 40px; color: ${C.cinza};
  font-size: clamp(15px, 2.2vw, 18px); line-height: 1.6;
`;

/* ---------- GALERIA LAZER ---------- */
const Grid = styled.div`
  display: grid; gap: 14px; grid-template-columns: 1fr;
  @media (min-width: 760px) { grid-template-columns: repeat(3, 1fr); gap: 12px; }
`;
const Tile = styled.figure`
  position: relative; margin: 0; border-radius: 14px; overflow: hidden;
  aspect-ratio: 4/3; box-shadow: 0 8px 22px rgba(0,40,80,0.12);
  img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .6s ease; }
  &:hover img { transform: scale(1.06); }
  figcaption {
    position: absolute; left: 0; right: 0; bottom: 0; padding: 28px 14px 12px;
    color: #fff; font-size: 13px; font-weight: 600; letter-spacing: 0.06em;
    background: linear-gradient(180deg, transparent, rgba(3,24,43,0.78));
  }
`;

/* ---------- VÍDEOS DA SEÇÃO DE LAZER (reels 9:16) ---------- */
const VideoRow = styled.div`
  display: flex; gap: 14px; overflow-x: auto; scroll-snap-type: x mandatory;
  padding: 4px 2px 14px; margin-top: 26px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin; scrollbar-color: rgba(0,40,80,0.25) transparent;
  &::-webkit-scrollbar { height: 8px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,40,80,0.22); border-radius: 4px; }
`;
const VideoCard = styled.div`
  flex: 0 0 auto; width: clamp(200px, 60vw, 240px); aspect-ratio: 9/16;
  scroll-snap-align: center; position: relative; border-radius: 16px; overflow: hidden;
  background: #06243d; box-shadow: 0 12px 30px rgba(0,40,80,0.18);
  img, video { width: 100%; height: 100%; object-fit: cover; display: block; }
  @media (min-width: 760px) { width: 250px; scroll-snap-align: start; }
`;
const VideoCardPoster = styled.button`
  position: absolute; inset: 0; border: 0; padding: 0; cursor: pointer; color: #fff;
  background: ${p => `url('${p.$poster}') center / cover no-repeat`};
  display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-end;
  &::before { content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(3,24,43,0.1) 40%, rgba(3,24,43,0.78)); transition: background .2s; }
  &:hover::before { background: linear-gradient(180deg, rgba(3,24,43,0.05) 30%, rgba(3,24,43,0.7)); }
  .vc-play { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1;
    width: 54px; height: 54px; border-radius: 50%; background: ${C.laranja};
    display: grid; place-items: center; font-size: 20px;
    box-shadow: 0 6px 22px rgba(245,130,32,0.55); }
  .vc-label { position: relative; z-index: 1; padding: 14px; text-align: left;
    font-size: 13px; font-weight: 700; letter-spacing: 0.04em;
    text-shadow: 0 2px 8px rgba(0,0,0,0.5); }
`;

/* ---------- CLUBES (cards clicáveis → galeria) ---------- */
const ClubGrid = styled.div`
  display: grid; gap: 16px; grid-template-columns: 1fr;
  @media (min-width: 760px) { grid-template-columns: 1fr 1fr; gap: 18px; }
`;
const ClubCard = styled.button`
  position: relative; border: 0; padding: 0; margin: 0; cursor: pointer;
  border-radius: 18px; overflow: hidden; aspect-ratio: 16/11; width: 100%;
  box-shadow: 0 14px 36px rgba(0,40,80,0.18); background: #06243d;
  img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .7s ease; }
  &::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(3,24,43,0) 38%, rgba(3,24,43,0.82) 100%);
  }
  &:hover img { transform: scale(1.05); }
  &:hover .club-meta { transform: translateY(-4px); }
`;
const ClubMeta = styled.span`
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 1; text-align: left;
  display: flex; flex-direction: column; align-items: flex-start;
  padding: 22px 22px 20px; color: #fff; transition: transform .25s ease;
  .nome { font-family: 'Cormorant Garamond', serif; font-style: italic; font-weight: 700;
          font-size: clamp(24px, 4.6vw, 34px); line-height: 1; }
  .ver { display: inline-flex; align-items: center; gap: 7px; margin-top: 10px;
         font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
         color: ${C.cyan}; }
  .ver span.dot { width: 26px; height: 26px; border-radius: 50%; background: ${C.laranja};
                  display: grid; place-items: center; font-size: 13px; color: #fff; }
`;

/* ---------- LIGHTBOX (galeria de fotos) ---------- */
const LbOverlay = styled.div`
  position: fixed; inset: 0; z-index: 60; background: rgba(3,16,28,0.94);
  display: flex; align-items: center; justify-content: center;
  animation: lbIn .2s ease; @keyframes lbIn { from { opacity: 0; } to { opacity: 1; } }
`;
const LbStage = styled.div`
  position: relative; width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center; padding: 56px 12px 70px;
`;
const LbImg = styled.img`
  max-width: 96vw; max-height: 100%; object-fit: contain;
  border-radius: 10px; box-shadow: 0 20px 60px rgba(0,0,0,0.5);
`;
const LbBtn = styled.button`
  position: absolute; z-index: 1; top: 50%; transform: translateY(-50%);
  width: 50px; height: 50px; border-radius: 50%; border: 0; cursor: pointer;
  background: rgba(255,255,255,0.12); color: #fff; font-size: 24px; line-height: 1;
  display: grid; place-items: center; backdrop-filter: blur(4px);
  transition: background .15s ease;
  &:hover { background: ${C.laranja}; }
  &.prev { left: 14px; } &.next { right: 14px; }
  @media (min-width: 768px) { width: 58px; height: 58px; left: 24px; &.next { right: 24px; left: auto; } }
`;
const LbClose = styled.button`
  position: absolute; top: 16px; right: 16px; z-index: 2;
  width: 44px; height: 44px; border-radius: 50%; border: 0; cursor: pointer;
  background: rgba(255,255,255,0.12); color: #fff; font-size: 22px;
  display: grid; place-items: center; backdrop-filter: blur(4px);
  &:hover { background: ${C.laranja}; }
`;
const LbCaption = styled.div`
  position: absolute; bottom: 22px; left: 0; right: 0; z-index: 2; text-align: center;
  color: rgba(255,255,255,0.9); font-size: 13px; letter-spacing: 0.06em;
  .t { font-weight: 700; color: #fff; }
`;

/* ---------- COMÉRCIO / GASTRONOMIA ---------- */
const ChipsRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; margin-top: 26px;
`;
const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px; border-radius: 999px; background: #fff;
  border: 1px solid #e0edf6; color: ${C.tinta}; font-size: 14px; font-weight: 600;
  box-shadow: 0 4px 14px rgba(0,40,80,0.06);
  .ic { color: ${C.laranja}; }
`;

/* ---------- DIFERENCIAIS ---------- */
const DiffSection = styled.section`
  background: linear-gradient(180deg, #0a3d6b, ${C.azul});
  color: #fff; padding: 70px 22px; @media (min-width: 768px) { padding: 92px 32px; }
`;
const DiffInner = styled.div` max-width: 1100px; margin: 0 auto; `;
const DiffGrid = styled.div`
  display: grid; gap: 16px; grid-template-columns: 1fr 1fr; margin-top: 38px;
  @media (min-width: 760px) { grid-template-columns: repeat(3, 1fr); gap: 20px; }
`;
const DiffItem = styled.div`
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 16px; padding: 22px 18px; text-align: center;
  .ic { font-size: 26px; margin-bottom: 12px; }
  .t { font-weight: 700; font-size: clamp(15px, 2.4vw, 17px); margin-bottom: 6px; }
  .s { font-size: 13px; line-height: 1.5; color: rgba(255,255,255,0.78); }
`;

/* ---------- TOUR 360 ---------- */
const TourWrap = styled.div`
  position: relative; border-radius: 18px; overflow: hidden;
  aspect-ratio: 16/9; background: #06243d;
  box-shadow: 0 20px 50px rgba(0,40,80,0.2);
`;
const TourPoster = styled.button`
  position: absolute; inset: 0; border: 0; cursor: pointer; color: #fff;
  background: url('/riviera/fotos/iate-26.jpg') center / cover no-repeat;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px;
  /* leve "panorâmica" pra sugerir que dá pra olhar em volta */
  animation: tourPan 22s ease-in-out infinite alternate;
  @keyframes tourPan { from { background-position: 35% center; } to { background-position: 65% center; } }
  @media (prefers-reduced-motion: reduce) { animation: none; }
  &::before { content: ''; position: absolute; inset: 0;
    background: radial-gradient(120% 90% at 50% 45%, rgba(3,24,43,0.22), rgba(3,24,43,0.62)); }
`;
/* Selo 360° no canto — deixa claro que é interativo, não vídeo */
const TourBadge = styled.span`
  position: absolute; top: 14px; left: 14px; z-index: 2;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 14px; border-radius: 999px;
  background: rgba(245,130,32,0.95); color: #fff;
  font-size: 12px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase;
  box-shadow: 0 6px 18px rgba(245,130,32,0.45);
  @media (min-width: 768px) { top: 18px; left: 18px; font-size: 13px; }
`;
const PlayDot = styled.span`
  position: relative; z-index: 1; width: 84px; height: 84px; border-radius: 50%;
  background: ${C.laranja}; display: grid; place-items: center; font-size: 30px;
  box-shadow: 0 8px 30px rgba(245,130,32,0.5);
  /* pulso pra chamar o clique */
  &::after { content: ''; position: absolute; inset: -10px; border-radius: 50%;
    border: 2px solid rgba(245,130,32,0.6); animation: tourPulse 2s ease-out infinite; }
  @keyframes tourPulse { 0% { transform: scale(0.9); opacity: 0.8; }
    100% { transform: scale(1.4); opacity: 0; } }
  @media (prefers-reduced-motion: reduce) { &::after { animation: none; } }
`;
const PlayLabel = styled.span`
  position: relative; z-index: 1; font-size: 15px; font-weight: 700; letter-spacing: 0.16em;
  text-transform: uppercase; text-shadow: 0 2px 10px rgba(0,0,0,0.5);
`;
const TourHint = styled.span`
  position: relative; z-index: 1; font-size: 12.5px; font-weight: 500; letter-spacing: 0.05em;
  opacity: 0.92; display: inline-flex; align-items: center; gap: 8px;
  text-shadow: 0 2px 8px rgba(0,0,0,0.5);
`;
const TourFrame = styled.iframe`
  position: absolute; inset: 0; width: 100%; height: 100%; border: 0;
`;

/* ---------- PROVA SOCIAL ---------- */
const ProofSection = styled.section`
  background: linear-gradient(180deg, ${C.azul}, ${C.azulEscuro});
  color: #fff; padding: 70px 22px;
  @media (min-width: 768px) { padding: 96px 32px; }
`;
const ProofInner = styled.div` max-width: 1180px; margin: 0 auto; `;
const CarouselWrap = styled.div`
  position: relative; margin-top: 40px;
  /* full-bleed: estende além do padding lateral do ProofSection */
  margin-left: calc(50% - 50vw); margin-right: calc(50% - 50vw);
`;
const Carousel = styled.div`
  display: flex; gap: 14px; overflow-x: auto; scroll-snap-type: x mandatory;
  padding: 8px max(22px, calc(50vw - 590px)) 24px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.28) transparent;
  &::-webkit-scrollbar { height: 8px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.28); border-radius: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
`;
// Grade estática para poucos registros: lado a lado, largura toda da coluna.
const ProofGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px;
  max-width: 720px; margin: 32px auto 8px; padding: 0 16px;
  /* o cartão tem largura fixa de carrossel; aqui ele ocupa a coluna */
  && > * { width: 100%; min-width: 0; }
`;
const ProofCardBox = styled.div`
  flex: 0 0 auto; width: clamp(220px, 72vw, 280px); aspect-ratio: 3/4;
  scroll-snap-align: center; position: relative; border-radius: 14px; overflow: hidden;
  background: #0a3d6b; box-shadow: 0 10px 28px rgba(0,0,0,0.35);
  img, video { width: 100%; height: 100%; object-fit: cover; display: block; }
  @media (min-width: 760px) { width: clamp(260px, 28vw, 320px); scroll-snap-align: start; }
`;
const VideoPoster = styled.button`
  position: absolute; inset: 0; border: 0; padding: 0; cursor: pointer; color: #fff;
  background: ${p => `url('${p.$poster}') center / cover no-repeat`};
  display: grid; place-items: center;
  &::before { content:''; position:absolute; inset:0; background: rgba(3,24,43,0.34); transition: background .2s; }
  &:hover::before { background: rgba(3,24,43,0.2); }
`;
const PlayBubble = styled.span`
  position: relative; z-index: 1; width: 60px; height: 60px; border-radius: 50%;
  background: ${C.laranja}; display: grid; place-items: center; font-size: 20px;
  box-shadow: 0 6px 22px rgba(245,130,32,0.55);
`;
const Hashtag = styled.div`
  text-align: center; color: ${C.cyan}; font-weight: 600; letter-spacing: 0.1em;
  margin-top: 8px; font-size: clamp(13px, 2.4vw, 16px);
`;

/* ---------- CONVENIÊNCIA E SERVIÇOS ---------- */
const SvcSection = styled.section`
  background: #fff; padding: 64px 22px;
  @media (min-width: 768px) { padding: 88px 32px; }
`;
const SvcGrid = styled.div`
  display: grid; gap: 12px; max-width: 1180px; margin: 32px auto 0;
  grid-template-columns: repeat(2, 1fr);
  @media (min-width: 760px) { grid-template-columns: repeat(4, 1fr); gap: 14px; }
`;
const SvcCard = styled.button`
  position: relative; margin: 0; padding: 0; border: 0; cursor: pointer; width: 100%;
  display: block; text-align: left; font-family: inherit;
  border-radius: 14px; overflow: hidden;
  aspect-ratio: 4/5; box-shadow: 0 6px 18px rgba(0,40,80,0.12);
  background: ${C.azulEscuro};
  img { width: 100%; height: 100%; object-fit: cover; display: block;
        transition: transform .5s ease; }
  &:hover img { transform: scale(1.05); }
  .cap {
    display: block; position: absolute; left: 0; right: 0; bottom: 0; padding: 32px 12px 12px;
    color: #fff; background: linear-gradient(180deg, transparent, rgba(3,24,43,0.86));
  }
  .cap .name { display: block; font-weight: 700; font-size: 14px; line-height: 1.2;
                     letter-spacing: 0.02em; }
  .cap .desc { display: block; font-size: 11.5px; opacity: 0.86; margin-top: 4px;
                     line-height: 1.35; }
  .cap .tag { display: inline-block; font-size: 10px; font-weight: 700;
                    letter-spacing: 0.08em; text-transform: uppercase;
                    background: ${C.laranja}; color: #fff;
                    padding: 3px 8px; border-radius: 999px; margin-top: 6px; }
  .count { position: absolute; top: 10px; right: 10px;
           display: inline-flex; align-items: center; gap: 5px;
           padding: 5px 9px; border-radius: 999px;
           background: rgba(3,24,43,0.62); color: #fff; font-size: 11px; font-weight: 700;
           backdrop-filter: blur(3px); }
  .count svg { width: 13px; height: 13px; }
  @media (min-width: 760px) {
    .cap .name { font-size: 15px; }
    .cap .desc { font-size: 12.5px; }
  }
`;

/* ---------- OFERTA ---------- */
const OfferBand = styled.section`
  background: ${C.laranja}; color: #fff; text-align: center; padding: 40px 22px;
`;
const OfferItems = styled.div`
  display: flex; flex-wrap: wrap; gap: 14px 30px; justify-content: center; align-items: center;
  font-size: clamp(15px, 2.4vw, 20px); font-weight: 600;
  span.sep { opacity: 0.6; }
`;

/* ---------- FORM ---------- */
const FormSection = styled.section`
  background: ${C.cream}; padding: 70px 22px 96px;
`;
const FormCard = styled.div`
  max-width: 520px; margin: 0 auto; background: #fff; border-radius: 20px;
  padding: 32px 24px; box-shadow: 0 20px 60px rgba(0,40,80,0.14); border: 1px solid #e7f0f7;
`;
const Steps = styled.div`
  display: flex; gap: 8px; justify-content: center; margin-bottom: 22px;
  span { width: 34px; height: 4px; border-radius: 2px; background: #dce8f1; }
  span.on { background: ${C.laranja}; }
`;
const Field = styled.input`
  width: 100%; padding: 14px 16px; margin-bottom: 12px; border-radius: 12px;
  border: 1px solid #d8e6f0; font-size: 16px; font-family: inherit;
  background: #fff; color: ${C.tinta}; color-scheme: light;
  &::placeholder { color: ${C.cinza}; opacity: 0.85; }
  &:focus { outline: none; border-color: ${C.azul}; box-shadow: 0 0 0 3px rgba(0,91,170,0.12); }
`;
const Btn = styled.button`
  width: 100%; padding: 15px; border: 0; border-radius: 12px; cursor: pointer;
  background: ${C.laranja}; color: #fff; font-size: 16px; font-weight: 700; font-family: inherit;
  letter-spacing: 0.02em; transition: filter .2s ease;
  &:hover { filter: brightness(1.05); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const GhostBtn = styled.button`
  background: none; border: 0; color: ${C.cinza}; cursor: pointer; font-size: 14px;
  margin-top: 12px; font-family: inherit; width: 100%;
`;
const DayRow = styled.div`
  display: flex; gap: 8px; overflow-x: auto; padding-bottom: 8px; margin-bottom: 14px;
`;
const DayBtn = styled.button`
  flex: 0 0 auto; padding: 10px 14px; border-radius: 12px; cursor: pointer; font-family: inherit;
  border: 1px solid ${p => (p.$on ? C.azul : '#d8e6f0')};
  background: ${p => (p.$on ? C.azul : '#fff')}; color: ${p => (p.$on ? '#fff' : C.tinta)};
  font-size: 13px; font-weight: 600; text-align: center; line-height: 1.2;
`;
const Slots = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 18px;
`;
const Slot = styled.button`
  padding: 9px 0; border-radius: 10px; cursor: pointer; font-family: inherit; font-size: 13px;
  border: 1px solid ${p => (p.$on ? C.laranja : '#d8e6f0')};
  background: ${p => (p.$on ? 'rgba(245,130,32,0.12)' : '#fff')};
  color: ${p => (p.$on ? C.laranja : C.tinta)}; font-weight: 600;
`;
const ModeChoices = styled.div`
  display: grid; gap: 12px; grid-template-columns: 1fr; margin: 6px 0 10px;
  @media (min-width: 540px) { grid-template-columns: 1fr 1fr; }
`;
const ModeCard = styled.button`
  text-align: left; padding: 16px; border-radius: 14px; cursor: pointer;
  border: 1px solid #d8e6f0; background: #fff; font-family: inherit;
  display: flex; flex-direction: column; gap: 4px;
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
  &:hover { transform: translateY(-2px); border-color: ${C.azul}; box-shadow: 0 12px 28px rgba(0,40,80,0.12); }
  .icon { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center;
          font-size: 17px; margin-bottom: 4px; }
  .icon.blue { background: rgba(0,91,170,.10); color: ${C.azul}; }
  .icon.warm { background: rgba(245,130,32,.14); color: ${C.laranja}; }
  .t { font-weight: 700; color: ${C.azul}; font-size: 15px; }
  .s { color: ${C.cinza}; font-size: 12.5px; line-height: 1.45; }
`;
const SkipBtn = styled.button`
  background: none; border: 0; color: ${C.cinza}; cursor: pointer; font-size: 13px;
  margin-top: 4px; width: 100%; font-family: inherit;
  text-decoration: underline; text-underline-offset: 3px;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const StepLead = styled.p`
  color: ${C.cinza}; font-size: 14px; text-align: center; margin: 0 0 14px;
`;
const ErrorText = styled.p`
  color: #b42318; background: #fff4f2; border: 1px solid #ffd6d0;
  border-radius: 10px; padding: 10px 12px; margin: 0 0 14px;
  font-size: 13px; line-height: 1.4; text-align: center;
`;
const PrivacyNote = styled.p`
  color: ${C.cinza}; font-size: 11.5px; line-height: 1.45; text-align: center;
  margin: 14px 4px 0;
  a { color: ${C.azul}; text-underline-offset: 2px; }
`;
const HoneypotField = styled.input`
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
`;
const Footer = styled.footer`
  background: ${C.azulEscuro}; color: rgba(255,255,255,0.7); text-align: center;
  padding: 36px 22px; font-size: 13px;
  img.logo { height: 26px; opacity: 0.9; margin-bottom: 14px; filter: brightness(0) invert(1); }
`;
const Corretor = styled.div`
  margin: 0 auto 16px; padding-bottom: 16px; max-width: 360px;
  border-bottom: 1px solid rgba(255,255,255,0.14);
  .nome { color: #fff; font-weight: 700; font-size: 15px; letter-spacing: 0.02em; }
  .creci { color: rgba(255,255,255,0.6); font-size: 12.5px; margin-top: 2px; }
  a.insta {
    display: inline-flex; align-items: center; gap: 8px; margin-top: 12px;
    padding: 9px 16px; border-radius: 999px; text-decoration: none;
    background: rgba(255,255,255,0.08); color: #fff; font-weight: 600; font-size: 13px;
    transition: background .15s ease;
    &:hover { background: ${C.laranja}; }
  }
`;

const INSTAGRAM_URL = 'https://www.instagram.com/corretorfabionogueira/';

/* ---------- CTA FLUTUANTE (discreto) ---------- */
const FloatCTA = styled.a`
  position: fixed; z-index: 50; right: 16px; bottom: 16px;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 18px; border-radius: 999px;
  background: ${C.laranja}; color: #fff; text-decoration: none;
  font-size: 13.5px; font-weight: 700; letter-spacing: 0.01em;
  box-shadow: 0 10px 26px rgba(245,130,32,0.4), 0 2px 8px rgba(0,0,0,0.22);
  opacity: ${p => (p.$show ? 1 : 0)};
  transform: translateY(${p => (p.$show ? '0' : '18px')});
  pointer-events: ${p => (p.$show ? 'auto' : 'none')};
  transition: opacity .3s ease, transform .3s ease, filter .15s ease;
  &:hover { filter: brightness(1.06); }
  svg { width: 1.05em; height: 1.05em; }
  @media (min-width: 768px) { right: 24px; bottom: 24px; padding: 13px 21px; font-size: 14px; }
`;

const WEEK = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Disponibilidade do produto (disponibilidade_v2): Seg–Sex 09:00–18:00, slots de 30min.
function nextWeekdays(n) {
  const out = [];
  const d = new Date();
  while (out.length < n) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd >= 1 && wd <= 5) out.push(new Date(d));
  }
  return out;
}
function buildSlots() {
  const out = [];
  for (let h = 9; h < 18; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    out.push(`${String(h).padStart(2, '0')}:30`);
  }
  return out;
}

const TRACKING_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
];

const RIVIERA_GOOGLE_ADS_TAG_ID = 'AW-18263020669';
const RIVIERA_GOOGLE_ADS_CONVERSION_DESTINATION =
  'AW-18263020669/0tV0CL3X984cEP2ovoRE';
const RIVIERA_META_PIXEL_ID = '1068786552034409';
// GA4 "Riviera XIII". Vive aqui, e nao no index.html, porque este build serve tres
// dominios (seulotenarepresa.com.br, mfrealstate.com.br e abianca.com.br) e so as
// paginas da Riviera podem coletar nesta propriedade.
const RIVIERA_GA4_MEASUREMENT_ID = 'G-GG611SV9PF';
const RIVIERA_GOOGLE_ADS_HOSTS = new Set([
  'seulotenarepresa.com.br',
  'www.seulotenarepresa.com.br',
]);

function isRivieraGoogleAdsPage() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return RIVIERA_GOOGLE_ADS_HOSTS.has(host) && path === '/gg';
}

function isRivieraPage() {
  if (typeof window === 'undefined') return false;
  return RIVIERA_GOOGLE_ADS_HOSTS.has(window.location.hostname.toLowerCase());
}

// Um unico gtag.js serve varios destinos: quem chega depois so acrescenta um
// `config`. Por isso Ads (AW-) e GA4 (G-) passam por aqui em vez de cada um
// injetar o seu script — dois gtag.js na mesma pagina fazem a Meta/Google
// contar duas vezes e atrasam o primeiro hit.
function ensureRivieraGtagDestination(tagId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  if (!document.querySelector('script[data-riviera-google-ads]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
    script.dataset.rivieraGoogleAds = 'true';
    document.head.appendChild(script);
  }

  const configurados = window.__rivieraGtagConfigured || (window.__rivieraGtagConfigured = {});
  if (!configurados.js) {
    window.gtag('js', new Date());
    configurados.js = true;
  }
  if (!configurados[tagId]) {
    window.gtag('config', tagId);
    configurados[tagId] = true;
  }
}

function ensureRivieraGoogleAdsTag() {
  if (!isRivieraGoogleAdsPage()) return false;
  ensureRivieraGtagDestination(RIVIERA_GOOGLE_ADS_TAG_ID);
  return true;
}

// Ao contrario do Ads, que so interessa na /gg (a landing paga), a propriedade do
// GA4 mede o site da Riviera inteiro — entao o recorte aqui e so o dominio.
function ensureRivieraGa4Tag() {
  if (!isRivieraPage()) return false;
  ensureRivieraGtagDestination(RIVIERA_GA4_MEASUREMENT_ID);
  return true;
}

function normalizeBrazilianPhoneForGoogleAds(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return digits.startsWith('55') ? `+${digits}` : `+55${digits}`;
}

function trackRivieraGoogleLeadConversion(lead, requestId) {
  if (!ensureRivieraGoogleAdsTag()) return;

  // Enhanced conversions: the Google tag hashes these values before sending.
  // E-mail is optional in this form, so only send the phone alongside a valid
  // e-mail, which is the supported identifier combination for this setup.
  const email = String(lead?.email || '').trim().toLowerCase();
  const phoneNumber = normalizeBrazilianPhoneForGoogleAds(lead?.telefone);
  if (email) {
    window.gtag('set', 'user_data', {
      email,
      ...(phoneNumber ? { phone_number: phoneNumber } : {}),
    });
  }

  window.gtag('event', 'conversion', {
    send_to: RIVIERA_GOOGLE_ADS_CONVERSION_DESTINATION,
    ...(requestId ? { transaction_id: requestId } : {}),
  });
}

function isRivieraMetaAdsPage() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return RIVIERA_GOOGLE_ADS_HOSTS.has(host) && path === '/meta';
}

function ensureRivieraMetaPixel() {
  if (!isRivieraMetaAdsPage()) return false;

  if (!window.fbq) {
    const fbq = function fbq() {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
  }

  if (!document.querySelector('script[data-riviera-meta-pixel]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.dataset.rivieraMetaPixel = 'true';
    document.head.appendChild(script);
  }

  if (!window.__rivieraMetaPixelConfigured) {
    window.fbq('init', RIVIERA_META_PIXEL_ID);
    window.fbq('track', 'PageView');
    window.__rivieraMetaPixelConfigured = true;
  }

  return true;
}

function cookieValue(name) {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const item = document.cookie
    .split(';')
    .map(value => value.trim())
    .find(value => value.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : '';
}

function getTrackingPayload(includeGoogleBraids = false) {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const keys = includeGoogleBraids
    ? [...TRACKING_KEYS, 'gbraid', 'wbraid']
    : TRACKING_KEYS;
  return keys.reduce((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
}

function getMetaTrackingPayload() {
  if (typeof window === 'undefined') return {};
  const tracking = getTrackingPayload();
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  const existingFbc = cookieValue('_fbc');
  const existingFbp = cookieValue('_fbp');

  return {
    ...tracking,
    ...(existingFbc
      ? { fbc: existingFbc }
      : fbclid ? { fbc: `fb.1.${Date.now()}.${fbclid}` } : {}),
    ...(existingFbp ? { fbp: existingFbp } : {}),
  };
}

const fotos = (prefix, n) =>
  Array.from({ length: n }, (_, i) => `/riviera/fotos/${prefix}-${String(i + 1).padStart(2, '0')}.jpg`);

// Clubes — cards grandes clicáveis que abrem a galeria (lightbox) do clube.
const CLUBES = [
  { key: 'marina', nome: 'Clube Marina', cover: 'marina-06',
    sub: 'Praia privativa, piscinas e marina', fotos: fotos('marina', 32) },
  { key: 'iate', nome: 'Iate Clube XIII', cover: 'iate-26',
    sub: 'Piscinas de borda infinita e náutica', fotos: fotos('iate', 32) },
];

// Amenities à beira da represa (grid; vídeos entram na Onda 2).
const LAZER = [
  { img: 'marina-26', cap: 'Praia privativa' },
  { img: 'iate-27', cap: 'Piscinas de borda infinita' },
  // Náutica clicável (Fábio pediu "hangar de barcos, lanchas e jets"). Fotos novas que
  // ele mandou 16/jun (marina aérea branded, lanchas no píer, hangar) + jet-ski do acervo.
  { img: 'nautica-marina', cap: 'Náutica na represa',
    fotos: ['nautica-marina', 'nautica-lanchas', 'nautica-hangar', 'orla-03'].map((f) => `/riviera/fotos/${f}.jpg`) },
  { img: 'lagos-03', cap: 'Lagos de pesca' },
  { img: 'orla-07', cap: 'Parque da Orla' },
];

// Galeria de vídeos (reels 9:16, curados do zip RSC XIII).
const VIDEOS = [
  { src: '/riviera/videos/lazer-aereo.mp4', poster: '/riviera/videos/lazer-aereo.jpg', label: 'Vista aérea do empreendimento' },
  { src: '/riviera/videos/lazer-marina.mp4', poster: '/riviera/videos/lazer-marina.jpg', label: 'Clube Marina' },
  { src: '/riviera/videos/lazer-iate.mp4', poster: '/riviera/videos/lazer-iate.jpg', label: 'Iate Clube XIII' },
  { src: '/riviera/videos/lazer-criancas.mp4', poster: '/riviera/videos/lazer-criancas.jpg', label: 'Festa das Crianças' },
  { src: '/riviera/videos/lazer-julina.mp4', poster: '/riviera/videos/lazer-julina.jpg', label: 'Festa Julina' },
  { src: '/riviera/videos/lazer-reveillon.mp4', poster: '/riviera/videos/lazer-reveillon.jpg', label: 'Réveillon na Riviera' },
];

// Diferenciais (#12) — texto enviado pelo Fábio.
// Correções pelo Laerte (via Fábio 09/jun):
//   "Condomínio fechado" → "Loteamento fechado"
//   "Segurança 24h"      → "Controle de acesso 24h"
//   "Moradia de alto padrão" → "Qualidade de vida"
const DIFERENCIAIS = [
  { ic: 'lock', t: 'Loteamento fechado', s: 'Acesso restrito a moradores e convidados, tranquilidade pra família.' },
  { ic: 'shield', t: 'Controle de acesso 24h', s: 'Portaria social + portaria de serviço, monitoramento de alarme gratuito.' },
  { ic: 'trending', t: 'Alta valorização', s: 'Administrado pela Momentum há 45+ anos, em região em crescimento.' },
  { ic: 'leaf', t: 'Estilo de vida diferenciado', s: 'A tranquilidade do campo com as atrações da represa.' },
  { ic: 'home', t: 'Qualidade de vida', s: 'Tudo que sua família merece à beira da maior represa de SP.' },
  { ic: 'anchor', t: 'Vida à beira d’água', s: 'Lazer náutico e praias de água doce internas.' },
];

// Prova social — batch enviado pelo Fábio (22/jun). Substitui por completo as fotos/vídeos
// antigos do carrossel. São registros reais de famílias batendo o gongo ao fechar o lote
// (compradores nacionais e internacionais — Boston, Seattle, Espanha). Vídeos e fotos
// INTERCALADOS: abre com vídeo do gongo, mas alterna foto a cada 2-3 cards (Fábio não quer
// "só vídeo no começo"). `vid` = clica e toca inline; `img` = foto estática.
const sm = (f) => `/riviera/social/${f}`;
const PROVA = [
  { t: 'vid', src: sm('gong-familia.mp4'),    poster: sm('gong-familia.jpg'),  label: 'Bateu o gongo em família' },
  { t: 'img', src: sm('gong-champanhe.jpg') },                                  // brinde de champanhe na assinatura
  { t: 'vid', src: sm('gong-videocall.mp4'),  poster: sm('gong-videocall.jpg'), label: 'Comprou de longe' },
  { t: 'vid', src: sm('gong-seattle.mp4'),    poster: sm('gong-seattle.jpg'),  label: 'Família de Seattle' },
  { t: 'img', src: sm('gong-casais.jpg') },                                     // dois casais com a maquete do loteamento
  { t: 'vid', src: sm('gong-boston.mp4'),     poster: sm('gong-boston.jpg'),   label: 'Família de Boston' },
  { t: 'vid', src: sm('gong-dupla.mp4'),      poster: sm('gong-dupla.jpg'),    label: 'Mais um lote fechado' },
  { t: 'img', src: sm('familia-deck.jpg') },                                    // família no deck, palmeiras à beira da represa
  { t: 'vid', src: sm('gong-espanha.mp4'),    poster: sm('gong-espanha.jpg'),  label: 'Família da Espanha' },
  { t: 'vid', src: sm('gong-cerimonia.mp4'),  poster: sm('gong-cerimonia.jpg'), label: 'A cerimônia do gongo' },
  { t: 'img', src: sm('assinatura-mesa.jpg') },                                 // jovens assinando o contrato
  { t: 'vid', src: sm('brinde-deck.mp4'),     poster: sm('brinde-deck.jpg'),   label: 'Brinde à nova conquista' },
  { t: 'vid', src: sm('gong-bebe.mp4'),       poster: sm('gong-bebe.jpg'),     label: 'A próxima geração' },
];

// Lightbox de galeria — recebe { titulo, fotos:[src], idx }. Setas + teclado + swipe.
function Lightbox({ gallery, onClose, onNav }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onNav(1);
      else if (e.key === 'ArrowLeft') onNav(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose, onNav]);

  const touch = useRef(null);
  if (!gallery) return null;
  const { titulo, fotos: imgs, idx } = gallery;
  return (
    <LbOverlay onClick={onClose}>
      <LbClose onClick={onClose} aria-label="Fechar galeria">{I.close}</LbClose>
      <LbStage
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touch.current == null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          if (Math.abs(dx) > 45) onNav(dx < 0 ? 1 : -1);
          touch.current = null;
        }}
      >
        <LbBtn className="prev" onClick={() => onNav(-1)} aria-label="Foto anterior">{I.chevL}</LbBtn>
        <LbImg src={imgs[idx]} alt={`${titulo} — foto ${idx + 1}`} />
        <LbBtn className="next" onClick={() => onNav(1)} aria-label="Próxima foto">{I.chevR}</LbBtn>
        <LbCaption><span className="t">{titulo}</span> · {idx + 1} / {imgs.length}</LbCaption>
      </LbStage>
    </LbOverlay>
  );
}

// Card de vídeo da seção de lazer (reels 9:16) — clica e toca inline.
function LazerVideo({ item }) {
  const [play, setPlay] = useState(false);
  return (
    <VideoCard>
      {play ? (
        <video src={item.src} autoPlay controls playsInline />
      ) : (
        <VideoCardPoster $poster={item.poster} onClick={() => setPlay(true)} aria-label={`Tocar vídeo: ${item.label}`}>
          <span className="vc-play">{I.play}</span>
          <span className="vc-label">{item.label}</span>
        </VideoCardPoster>
      )}
    </VideoCard>
  );
}

function ProofCard({ item }) {
  const [play, setPlay] = useState(false);
  if (item.t === 'vid') {
    return (
      <ProofCardBox>
        {play ? (
          <video src={item.src} autoPlay controls playsInline loop />
        ) : (
          <VideoPoster $poster={item.poster} onClick={() => setPlay(true)} aria-label={`Tocar vídeo: ${item.label}`}>
            <PlayBubble>{I.play}</PlayBubble>
          </VideoPoster>
        )}
      </ProofCardBox>
    );
  }
  return (
    <ProofCardBox>
      <img src={item.src} alt="Família feliz na Riviera de Santa Cristina XIII" loading="lazy" />
    </ProofCardBox>
  );
}

// Conveniência e Serviços — fotos do Fábio (09/jun + batch 14/jun).
// Cada estabelecimento abre galeria (lightbox) com várias fotos, igual aos clubes.
const svc = (f) => `/riviera/fotos/servicos/${f}.jpg`;
// Fotos recuradas a partir do Drive organizado pelo Fábio (15/jun) — pastas por
// estabelecimento, sem mistura. 5 com galeria de 4 fotos; demais com 1 foto.
const SERVICOS = [
  { name: 'Casa do Lago',     desc: 'Restaurante e cafeteria à beira do lago',
    fotos: ['casa-do-lago-1', 'casa-do-lago-2', 'casa-do-lago-3', 'casa-do-lago-4'].map(svc) },
  { name: 'Ponto Alto',       desc: 'Restaurante, pizzaria e hamburgueria',
    fotos: ['ponto-alto-1', 'ponto-alto-2', 'ponto-alto-3', 'ponto-alto-4'].map(svc) },
  { name: 'Empório',          desc: 'Mercado completo dentro do loteamento',
    fotos: ['emporio-1', 'emporio-2', 'emporio-3', 'emporio-4'].map(svc) },
  { name: 'Cacau Show',       desc: 'Cafeteria, fondue e chocolates',
    fotos: ['cacau-show-1', 'cacau-show-2', 'cacau-show-3', 'cacau-show-4'].map(svc) },
  { name: 'Fogão Caseiro',    desc: 'Restaurante de comida caseira',
    fotos: ['fogao-caseiro-1', 'fogao-caseiro-2', 'fogao-caseiro-3', 'fogao-caseiro-4'].map(svc) },
  { name: 'Mini Market',      desc: 'Conveniência pro dia a dia',
    fotos: ['mini-market-fachada'].map(svc) },
  { name: 'Heliponto',        desc: 'Heliponto no loteamento', fotos: ['heliponto'].map(svc) },
  { name: 'Centro Comercial', desc: 'Novo centro comercial, entrega 2027', tag: 'Em obras',
    fotos: ['centro-comercial-obras-01'].map(svc) },
  { name: 'Nova Piscina',     desc: 'Novo complexo de piscinas à beira da represa', tag: 'Em breve',
    fotos: ['nova-piscina'].map(svc) },
  { name: 'Academia',         desc: 'Entrega prevista pra julho/2026', tag: 'Em obras',
    fotos: ['academia-obras'].map(svc) },
];

// Na rota reserva (tarefa 6f99acda) nada pode citar a Momentum: pedido do Fábio
// em 22/09, "aonde tiver escrito Momentum a gente tem que tirar". O texto neutro
// diz o mesmo sem a marca.
const DIFERENCIAIS_RESERVA = DIFERENCIAIS.map((d) =>
  d.ic === 'trending' ? { ...d, s: 'Loteamento consolidado, em região em crescimento.' } : d
);

export default function RivieraLP({ flow = 'default', variant = 'principal' }) {
  const isReserva = variant === 'reserva';
  useNoIndex(isReserva);
  const provaVisivel = isReserva ? PROVA.filter((p) => PROVA_RESERVA.has(p.src)) : PROVA;
  // As páginas Terrenos/Casas mandam para #agendar quando a lista está vazia;
  // o router não rola até a âncora sozinho.
  useEffect(() => {
    if (window.location.hash !== '#agendar') return undefined;
    const t = setTimeout(() => document.getElementById('agendar')?.scrollIntoView({ block: 'start' }), 400);
    return () => clearTimeout(t);
  }, []);
  // Variante da faixa só para comparação na página de aprovação (?faixa=aviao).
  const faixaVariante = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('faixa') === 'aviao' ? 'aviao' : 'faixa';
  const isGoogleLead = flow === 'google-lead';
  const isMetaLead = flow === 'meta-lead';
  const isPaidLead = isGoogleLead || isMetaLead;
  const [step, setStep] = useState(1);
  const [lead, setLead] = useState({ nome: '', telefone: '', email: '' });
  const [website, setWebsite] = useState('');
  const paidRequestIdRef = useRef(null);
  const [modality, setModality] = useState(null); // 'online' | 'presencial' | 'material'
  const [dayIdx, setDayIdx] = useState(0);
  const [slot, setSlot] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showTour, setShowTour] = useState(false);

  // CTA flutuante discreto: aparece depois do hero, some quando o form entra em tela.
  const [showCTA, setShowCTA] = useState(false);
  useEffect(() => {
    const form = document.getElementById('agendar');
    let formVisible = false;
    const update = () => setShowCTA(window.scrollY > window.innerHeight * 0.9 && !formVisible);
    const io = form
      ? new IntersectionObserver(([e]) => { formVisible = e.isIntersecting; update(); }, { threshold: 0.04 })
      : null;
    if (io && form) io.observe(form);
    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => { window.removeEventListener('scroll', update); if (io) io.disconnect(); };
  }, []);

  // Galeria/lightbox: { titulo, fotos, idx } | null
  const [gallery, setGallery] = useState(null);
  const openGallery = (titulo, imgs, idx = 0) => setGallery({ titulo, fotos: imgs, idx });
  const navGallery = (dir) => setGallery(g => g && {
    ...g, idx: (g.idx + dir + g.fotos.length) % g.fotos.length,
  });

  // Hero video autoplay iOS-safe (padrão SofiaLP) — handle Low Power Mode + gesture retry
  const videoRef = useRef(null);
  const [autoplayFailed, setAutoplayFailed] = useState(false);

  useEffect(() => {
    document.title = isPaidLead
      ? 'Lotes na Represa Jurumirim | Riviera XIII'
      : 'Riviera de Santa Cristina XIII — Maravilhosa por Natureza';
  }, [isPaidLead]);

  useEffect(() => {
    if (!isPaidLead) return undefined;

    const html = document.documentElement;
    const previousLang = html.lang;
    html.lang = 'pt-BR';

    let description = document.querySelector('meta[name="description"]');
    const createdDescription = !description;
    if (!description) {
      description = document.createElement('meta');
      description.setAttribute('name', 'description');
      document.head.appendChild(description);
    }
    const previousDescription = description.getAttribute('content');
    description.setAttribute(
      'content',
      `Conheça os lotes da Riviera de Santa Cristina XIII, em Paranapanema, às margens da Represa Jurumirim. Condições a partir de R$ ${isMetaLead ? '160' : '150'} mil e até 180 meses.`
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const previousCanonical = canonical.getAttribute('href');
    canonical.setAttribute(
      'href',
      `https://www.seulotenarepresa.com.br/${isMetaLead ? 'meta' : 'gg'}`
    );

    return () => {
      html.lang = previousLang;
      if (createdDescription) description.remove();
      else if (previousDescription === null) description.removeAttribute('content');
      else description.setAttribute('content', previousDescription);
      if (createdCanonical) canonical.remove();
      else if (previousCanonical === null) canonical.removeAttribute('href');
      else canonical.setAttribute('href', previousCanonical);
    };
  }, [isMetaLead, isPaidLead]);

  useEffect(() => {
    if (isGoogleLead) ensureRivieraGoogleAdsTag();
  }, [isGoogleLead]);

  // Depois do Ads de proposito: na /gg quem injeta o gtag.js continua sendo o Ads
  // (`?id=AW-...`, como antes do GA4 existir) e aqui so entra o `config` do G-.
  useEffect(() => {
    ensureRivieraGa4Tag();
  }, []);

  useEffect(() => {
    if (isMetaLead) ensureRivieraMetaPixel();
  }, [isMetaLead]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true; v.defaultMuted = true;
    let succeeded = false;

    const onPlaying = () => { succeeded = true; };
    const onError = () => setAutoplayFailed(true);
    v.addEventListener('playing', onPlaying);
    v.addEventListener('error', onError);

    const p = v.play();
    if (p && typeof p.then === 'function') p.catch(() => setAutoplayFailed(true));

    const t = setTimeout(() => { if (!succeeded) setAutoplayFailed(true); }, 5000);

    // Fallback final: primeiro gesto do user satisfaz a política de autoplay do iOS
    const onFirstGesture = () => {
      if (succeeded || !videoRef.current) return cleanupGesture();
      const v2 = videoRef.current;
      v2.muted = true;
      const p2 = v2.play();
      if (p2 && typeof p2.then === 'function') {
        p2.then(() => setAutoplayFailed(false)).catch(() => {});
      }
      cleanupGesture();
    };
    const cleanupGesture = () => {
      window.removeEventListener('touchstart', onFirstGesture, true);
      window.removeEventListener('click', onFirstGesture, true);
      window.removeEventListener('scroll', onFirstGesture, true);
    };
    window.addEventListener('touchstart', onFirstGesture, { passive: true, capture: true });
    window.addEventListener('click', onFirstGesture, { capture: true });
    window.addEventListener('scroll', onFirstGesture, { passive: true, capture: true });

    return () => {
      clearTimeout(t);
      cleanupGesture();
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('error', onError);
    };
  }, []);

  const days = useMemo(() => nextWeekdays(8), []);
  const slots = useMemo(buildSlots, []);
  const step1ok = lead.nome.trim() && lead.telefone.trim();
  const emailValid = !lead.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim());

  const submit = async (modeOverride) => {
    if (submitting) return;

    if (isPaidLead) {
      setSubmitting(true);
      setSubmitError('');
      if (!paidRequestIdRef.current) {
        paidRequestIdRef.current = window.crypto?.randomUUID?.() || `${Date.now()}`;
      }

      try {
        const response = await fetch(isMetaLead ? '/api/riviera-meta' : '/api/riviera-google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...lead,
            page_url: window.location.href,
            referrer: document.referrer || null,
            request_id: paidRequestIdRef.current,
            tracking: isMetaLead ? getMetaTrackingPayload() : getTrackingPayload(true),
            website,
          }),
        });

        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body.success) {
          throw new Error('Não foi possível enviar agora. Tente novamente em instantes.');
        }

        if (body.action !== 'ignored') {
          window.dataLayer = window.dataLayer || [];
          if (isGoogleLead) {
            window.dataLayer.push({
              event: 'riviera_google_lead',
              lead_source: 'google_ads',
            });
            // O backend devolve o mesmo transaction_id usado pelo Data Manager.
            // Se ambos os sinais chegarem, o Google deduplica; se o navegador
            // fechar antes do ping, o outbox server-side preserva a conversão.
            trackRivieraGoogleLeadConversion(
              lead,
              body.transaction_id || paidRequestIdRef.current
            );
          } else {
            window.dataLayer.push({
              event: 'riviera_meta_lead',
              lead_source: 'meta_site',
            });
          }
        }
        paidRequestIdRef.current = null;
        setDone(true);
      } catch (error) {
        setSubmitError(
          error?.message || 'Não foi possível enviar agora. Tente novamente em instantes.'
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const m = modeOverride || modality || 'material';
    if (modeOverride) setModality(modeOverride);
    const escolhido = m && m !== 'material' ? days[dayIdx] : null;

    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/riviera-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...lead,
          modality: m,
          data: escolhido ? escolhido.toISOString() : null,
          slot: escolhido ? slot : null,
          page_url: window.location.href,
          referrer: document.referrer || null,
          utm: getTrackingPayload(),
        }),
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || 'Falha ao enviar');
      }

      setDone(true);
    } catch {
      setSubmitError('Não foi possível enviar seus dados agora. Tente novamente em alguns instantes.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      {/* HERO */}
      <Hero>
        <HeroVideo
          ref={videoRef}
          autoPlay
          loop
          muted
          defaultMuted
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          preload="metadata"
          disableRemotePlayback
          disablePictureInPicture
          controls={false}
          poster="/riviera/hero-peninsula.jpg"
          src="/riviera/hero-loop-kling-sky-2x.mp4"
        />
        {autoplayFailed && (
          <HeroPoster
            src="/riviera/hero-peninsula.jpg"
            alt=""
            aria-hidden="true"
            style={{ opacity: 1 }}
          />
        )}
        <HeroOverlay />
        <HeroTopBar>
          {isReserva ? <ReservaNav /> : <Eyebrow>Momentum</Eyebrow>}
        </HeroTopBar>
        {/* Faixa de oferta (tarefa a7586bc3). Em 25/09 o João deixou só na rota
            reserva; em 16/10 aprovou levar para o site principal (tarefa 87c272e9,
            bloco r1-faixa), depois de o Fábio dizer "pode pôr" na reunião de 15/10.
            Fica fora das landings de anúncio (/gg e /meta usam flow próprio): ali
            a oferta passaria pela revisão de anúncio e de compliance. */}
        {(isReserva || flow === 'default') && <FaixaOferta variante={faixaVariante} />}
        <HeroContent
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.3, ease: 'easeOut' }}
        >
          {isPaidLead ? (
            <>
              <GoogleHeroTitle>Lotes na Represa Jurumirim</GoogleHeroTitle>
              <GoogleHeroSub>
                Riviera de Santa Cristina XIII · Paranapanema, SP
              </GoogleHeroSub>
              <GoogleOfferChips aria-label="Condições comerciais">
                <span>A partir de R$ {isMetaLead ? '160' : '150'} mil</span>
                <span>Entrada facilitada</span>
                <span>Até 180 meses</span>
              </GoogleOfferChips>
            </>
          ) : (
            <>
              <HeroTitle>
                Riviera de Santa Cristina <Sail>XIII</Sail>
              </HeroTitle>
              <HeroSub>
                Represa Jurumirim, a maior e mais limpa represa do estado de São Paulo
              </HeroSub>
            </>
          )}
          <HeroCTA
            href="#agendar"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('agendar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            {isPaidLead ? 'Receber condições e disponibilidade' : 'Quero mais informações'}
          </HeroCTA>
        </HeroContent>
      </Hero>

      {/* INTERLÚDIO */}
      <Interlude>
        <IntLine1>{isPaidLead ? 'Às margens da' : 'À beira da'}</IntLine1>
        <IntLine2>{isPaidLead ? 'Represa Jurumirim' : 'maior represa de São Paulo'}</IntLine2>
      </Interlude>

      {/* LOCALIZAÇÃO — mapa scroll-driven pras cidades */}
      <div id="localizacao"><RivieraMapa /></div>

      {/* CLUBES — 2 cards padrão resort internacional, clicáveis → galeria */}
      <Section id="clubes">
        <Kicker>Estrutura de lazer completa</Kicker>
        <H2>{isPaidLead ? '2 clubes para aproveitar a Riviera' : '2 clubes com padrão de resort internacional'}</H2>
        <Lead>
          {isPaidLead
            ? 'Clube Marina e Iate Clube: praias privativas, piscinas, marina e hangar náutico, quadras de tênis e muito mais. Clique para ver as fotos.'
            : 'Clube Marina e Iate Clube XIII: praias privativas, piscinas de borda infinita, marina e hangar náutico, quadras de tênis e muito mais. Clique pra ver as fotos.'}
        </Lead>
        <ClubGrid>
          {CLUBES.map((c) => (
            <ClubCard
              key={c.key}
              onClick={() => openGallery(isPaidLead && c.key === 'iate' ? 'Iate Clube' : c.nome, c.fotos)}
              aria-label={`Ver fotos do ${isPaidLead && c.key === 'iate' ? 'Iate Clube' : c.nome}`}
            >
              <img
                src={`/riviera/fotos/${c.cover}.jpg`}
                alt={isPaidLead && c.key === 'iate' ? 'Iate Clube' : c.nome}
                loading="lazy"
              />
              <ClubMeta className="club-meta">
                <span className="nome">{isPaidLead && c.key === 'iate' ? 'Iate Clube' : c.nome}</span>
                <span className="ver"><span className="dot">{I.images}</span> Ver galeria de fotos</span>
              </ClubMeta>
            </ClubCard>
          ))}
        </ClubGrid>
      </Section>

      {/* AMENITIES à beira da represa */}
      <Section id="lazer" style={{ paddingTop: 0 }}>
        <Kicker>À beira da represa</Kicker>
        <H2>{isPaidLead ? 'Lazer às margens da Represa Jurumirim' : 'Lazer à beira da maior represa de SP'}</H2>
        <Lead>
          Praias de água doce, lagos de pesca, náutica e o Parque da Orla — a represa Jurumirim
          como quintal da sua família.
        </Lead>
        <Grid>
          {LAZER.map((it, i) => (
            <Tile
              key={i}
              as={it.fotos ? 'button' : 'figure'}
              onClick={it.fotos ? () => openGallery(it.cap, it.fotos) : undefined}
              aria-label={it.fotos ? `Ver fotos de ${it.cap}` : undefined}
              style={it.fotos ? { cursor: 'pointer', border: 0, padding: 0, font: 'inherit', width: '100%' } : undefined}
            >
              <img src={`/riviera/fotos/${it.img}.jpg`} alt={it.cap} loading="lazy" />
              {it.fotos && (
                <span style={{
                  position: 'absolute', top: 10, right: 10, zIndex: 1,
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 9px', borderRadius: 999, background: 'rgba(3,24,43,0.62)',
                  color: '#fff', fontSize: 11, fontWeight: 700, backdropFilter: 'blur(3px)',
                }}>{I.images} {it.fotos.length}</span>
              )}
              <figcaption>{it.cap}</figcaption>
            </Tile>
          ))}
        </Grid>
      </Section>

      {/* GALERIA DE VÍDEOS — carrossel reels 9:16 */}
      <Section style={{ paddingTop: 0 }}>
        <H2>Galeria de vídeos</H2>
        <Lead>
          Sobrevoe os clubes, sinta a vista aérea da represa e veja a Riviera viva nos
          eventos do loteamento.
        </Lead>
        <VideoRow>
          {VIDEOS.map((v, i) => <LazerVideo key={i} item={v} />)}
        </VideoRow>
      </Section>

      {/* GASTRONOMIA & CONVENIÊNCIA — 10 cards clicáveis → galeria (fotos Fábio 09+14/jun) */}
      <SvcSection>
        <Kicker>No loteamento</Kicker>
        <H2>Gastronomia & conveniência</H2>
        <Lead>
          Você não precisa sair pra nada. Da cafeteria à pizzaria, mini market, chocolate,
          heliponto, academia e centro comercial — tudo dentro da Riviera. Toque pra ver as fotos.
        </Lead>
        <SvcGrid>
          {SERVICOS.map((s, i) => (
            <SvcCard key={i} onClick={() => openGallery(s.name, s.fotos)} aria-label={`Ver fotos de ${s.name}`}>
              <img src={s.fotos[0]} alt={s.name} loading="lazy" />
              {s.fotos.length > 1 && (
                <span className="count">{I.images} {s.fotos.length}</span>
              )}
              <span className="cap">
                <span className="name">{s.name}</span>
                <span className="desc">{s.desc}</span>
                {s.tag && <span className="tag">{s.tag}</span>}
              </span>
            </SvcCard>
          ))}
        </SvcGrid>
      </SvcSection>

      {/* DIFERENCIAIS (#12) */}
      <DiffSection>
        <DiffInner>
          <Kicker style={{ color: C.cyan }}>Por que a Riviera</Kicker>
          <H2 style={{ color: '#fff' }}>Muito além de um lote</H2>
          <Lead style={{ color: 'rgba(255,255,255,0.82)' }}>
            {isPaidLead
              ? 'A tranquilidade do campo somada às atrações da represa, com controle de acesso 24h e a estrutura de um loteamento de alto padrão.'
              : 'A tranquilidade do campo somada às atrações da represa, com a segurança e a valorização de um loteamento de alto padrão.'}
          </Lead>
          <DiffGrid>
            {(isReserva ? DIFERENCIAIS_RESERVA : DIFERENCIAIS).map((d) => (
              <DiffItem key={d.t}>
                <div className="ic">{I[d.ic]}</div>
                <div className="t">{d.t}</div>
                <div className="s">{d.s}</div>
              </DiffItem>
            ))}
          </DiffGrid>
        </DiffInner>
      </DiffSection>

      {/* TOUR 360 */}
      <Section id="tour-360">
        <Kicker>Experiência interativa</Kicker>
        <H2>Passeie em 360° pela Riviera</H2>
        <Lead>
          {isPaidLead
            ? 'Não é vídeo: é um tour interativo. Clique, arraste para olhar em volta e caminhe pelo Iate Clube e pelos setores do empreendimento.'
            : 'Não é vídeo: é um tour interativo. Clique, arraste pra olhar em volta e caminhe pelo Iate Clube XIII e pelos setores do empreendimento — como se estivesse lá.'}
        </Lead>
        <TourWrap>
          {showTour ? (
            <TourFrame
              src="https://tour.meupasseiovirtual.com/view/Pa3o0XWXBK8"
              title="Tour 360 Riviera de Santa Cristina XIII"
              allow="fullscreen; accelerometer; gyroscope"
              allowFullScreen
            />
          ) : (
            <TourPoster onClick={() => setShowTour(true)} aria-label="Abrir tour 360 interativo">
              <TourBadge>{I.rotate} Tour 360° interativo</TourBadge>
              <PlayDot>{I.rotate}</PlayDot>
              <PlayLabel>Iniciar o tour 360°</PlayLabel>
              <TourHint>{I.drag} clique e arraste pra explorar</TourHint>
            </TourPoster>
          )}
        </TourWrap>
      </Section>

      {/* PROVA SOCIAL — carrossel grande com fotos + vídeos. Na reserva só entram
          os registros marcados em PROVA_RESERVA; lista vazia esconde a seção. */}
      {provaVisivel.length > 0 && (
      <ProofSection>
        <ProofInner>
          <Kicker style={{ color: C.cyan }}>#maisumafamiliafeliz</Kicker>
          <H2 style={{ color: '#fff' }}>Cada gongo, uma nova história</H2>
          <Lead style={{ color: 'rgba(255,255,255,0.82)' }}>
            Toda semana uma família fecha seu lote e bate o gongo da Riviera — o começo de uma vida
            de fins de semana à beira da maior represa de São Paulo. Veja quem já é daqui.
          </Lead>
        </ProofInner>
        {/* Com poucos registros (a reserva tem dois) o carrossel fica feio,
            palavra do João em 25/09: vira grade estática, sem "arraste". */}
        {provaVisivel.length <= 3 ? (
          <ProofGrid>
            {provaVisivel.map((it, i) => <ProofCard key={i} item={it} />)}
          </ProofGrid>
        ) : (
          <>
            <CarouselWrap>
              <Carousel>
                {provaVisivel.map((it, i) => <ProofCard key={i} item={it} />)}
              </Carousel>
            </CarouselWrap>
            <Hashtag>arraste pra ver mais {I.chevR}</Hashtag>
          </>
        )}
      </ProofSection>
      )}

      {/* OFERTA */}
      <OfferBand id="condicoes">
        <OfferItems>
          <span>Lotes a partir de R$ {isMetaLead ? '160' : '150'} mil</span>
          <span className="sep">·</span>
          <span>Entrada facilitada</span>
          <span className="sep">·</span>
          <span>Financiamento em até 180 meses</span>
        </OfferItems>
      </OfferBand>

      {/* FORM — fluxo padrão ou cadastro simples da variante Google */}
      <FormSection id="agendar">
        <Kicker>{isPaidLead ? 'Saiba mais' : 'Próximo passo'}</Kicker>
        <H2>Receba mais informações</H2>
        <Lead>
          {isPaidLead
            ? 'Deixe seu contato para receber todos os detalhes da Riviera de Santa Cristina XIII. Nossa equipe entrará em contato com você.'
            : 'Deixe seus dados pra receber o material completo. Depois você decide se prefere uma conversa online com um especialista ou uma visita à Riviera.'}
        </Lead>
        <FormCard>
          {done ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 44, marginBottom: 10, color: C.laranja }}>{I.sun}</div>
              <H2 style={{ fontSize: 26 }}>Recebemos seus dados!</H2>
              <p style={{ color: C.cinza }}>
                {isPaidLead
                  ? 'Nossa equipe entrará em contato para apresentar todos os detalhes.'
                  : (() => {
                  if (!modality || modality === 'material') {
                    return 'Em instantes a gente te envia o material completo no seu WhatsApp.';
                  }
                  const d = days[dayIdx];
                  const dia = `${WEEK[d.getDay()]} ${d.getDate()} ${MES[d.getMonth()]}`;
                  const tipo = modality === 'online'
                    ? 'Sua conversa online com nosso especialista'
                    : 'Sua visita à Riviera';
                  return `${tipo} está marcada pra ${dia} às ${slot}. Em instantes confirmamos no seu WhatsApp.`;
                })()}
              </p>
            </div>
          ) : isPaidLead ? (
            <>
              {submitError && <ErrorText>{submitError}</ErrorText>}
              <HoneypotField
                name="website"
                value={website}
                onChange={event => setWebsite(event.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <Field placeholder="Seu nome" value={lead.nome}
                onChange={e => setLead({ ...lead, nome: e.target.value })} />
              <Field placeholder="WhatsApp (DDD + número)" inputMode="tel" value={lead.telefone}
                onChange={e => setLead({ ...lead, telefone: e.target.value })} />
              <Field placeholder="E-mail (opcional)" type="email" value={lead.email}
                onChange={e => setLead({ ...lead, email: e.target.value })} />
              {lead.email && !emailValid && (
                <ErrorText>Digite um e-mail válido ou deixe o campo em branco.</ErrorText>
              )}
              <Btn disabled={!step1ok || !emailValid || submitting} onClick={() => submit()}>
                {submitting ? 'Enviando...' : 'Quero conhecer a Riviera'}
              </Btn>
              <PrivacyNote>
                Ao continuar, você concorda com o contato da equipe Riviera e com a nossa{' '}
                <a href="/privacy" target="_blank" rel="noreferrer">Política de Privacidade</a>.
              </PrivacyNote>
            </>
          ) : (
            <>
              <Steps>
                <span className={step >= 1 ? 'on' : ''} />
                <span className={step >= 2 ? 'on' : ''} />
                <span className={step >= 3 ? 'on' : ''} />
              </Steps>
              {submitError && <ErrorText>{submitError}</ErrorText>}
              {step === 1 && (
                <>
                  <Field placeholder="Seu nome" value={lead.nome}
                    onChange={e => setLead({ ...lead, nome: e.target.value })} />
                  <Field placeholder="WhatsApp (DDD + número)" inputMode="tel" value={lead.telefone}
                    onChange={e => setLead({ ...lead, telefone: e.target.value })} />
                  <Field placeholder="E-mail (opcional)" type="email" value={lead.email}
                    onChange={e => setLead({ ...lead, email: e.target.value })} />
                  <Btn disabled={!step1ok} onClick={() => setStep(2)}>Quero receber</Btn>
                </>
              )}
              {step === 2 && (
                <>
                  <StepLead>Quer dar mais um passo? Escolha como prefere continuar:</StepLead>
                  <ModeChoices>
                    <ModeCard onClick={() => { setModality('online'); setStep(3); }}>
                      <span className="icon blue">{I.chat}</span>
                      <span className="t">Conversa online com especialista</span>
                      <span className="s">Por WhatsApp ou videochamada, no horário que combinar.</span>
                    </ModeCard>
                    <ModeCard onClick={() => { setModality('presencial'); setStep(3); }}>
                      <span className="icon warm">{I.pin}</span>
                      <span className="t">Visita presencial à Riviera</span>
                      <span className="s">Conheça o empreendimento ao vivo, sem compromisso.</span>
                    </ModeCard>
                  </ModeChoices>
                  <SkipBtn disabled={submitting} onClick={() => { setModality('material'); submit('material'); }}>
                    {submitting ? 'Enviando...' : 'Só quero o material por enquanto'}
                  </SkipBtn>
                  <GhostBtn onClick={() => setStep(1)}>← voltar</GhostBtn>
                </>
              )}
              {step === 3 && (
                <>
                  <StepLead>
                    {modality === 'online'
                      ? 'Escolha o melhor dia e horário pra conversar:'
                      : 'Escolha o melhor dia e horário pra sua visita:'}
                  </StepLead>
                  <DayRow>
                    {days.map((d, i) => (
                      <DayBtn key={i} $on={i === dayIdx} onClick={() => { setDayIdx(i); setSlot(null); }}>
                        {WEEK[d.getDay()]}<br />{d.getDate()} {MES[d.getMonth()]}
                      </DayBtn>
                    ))}
                  </DayRow>
                  <Slots>
                    {slots.map(s => (
                      <Slot key={s} $on={slot === s} onClick={() => setSlot(s)}>{s}</Slot>
                    ))}
                  </Slots>
                  <Btn disabled={!slot || submitting} onClick={() => submit()}>
                    {submitting
                      ? 'Enviando...'
                      : modality === 'online' ? 'Confirmar conversa' : 'Confirmar visita'}
                  </Btn>
                  <GhostBtn onClick={() => { setStep(2); setSlot(null); }}>← voltar</GhostBtn>
                </>
              )}
            </>
          )}
        </FormCard>
      </FormSection>

      <Footer>
        <img className="logo" src="/riviera/logo.png" alt="Riviera de Santa Cristina XIII" />
        <Corretor>
          <div className="nome">Fábio Muniz Nogueira</div>
          <div className="creci">Corretor de imóveis · CRECI 220063-F</div>
          <a className="insta" href={INSTAGRAM_URL} target="_blank" rel="noreferrer">
            {I.instagram} Siga no Instagram
          </a>
        </Corretor>
        <div>{isReserva
          ? 'Riviera de Santa Cristina XIII · Maravilhosa por natureza'
          : 'Riviera de Santa Cristina XIII · Momentum · Maravilhosa por natureza'}</div>
      </Footer>

      <FloatCTA
        $show={showCTA}
        href="#agendar"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('agendar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        {I.calendar} {isPaidLead ? 'Receba informações' : 'Agende sua visita'}
      </FloatCTA>

      {gallery && (
        <Lightbox gallery={gallery} onClose={() => setGallery(null)} onNav={navGallery} />
      )}
    </Page>
  );
}
